import { Button } from '@/Components/ui/button';
import GatedButton from '@/Components/Grants/GatedButton';
import Checkbox from '@/Components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogTitle,
} from '@/Components/ui/dialog';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

// What a release means, by whether the passport was released before: bill
// once, so only a first release is recorded for billing.
const RELEASE_TERMS = {
    first: "A release is permanent: this first release is recorded for billing, and the passport's values and company assigned ID are locked.",
    again: "A release is permanent, and the passport's values are locked again. Only a passport's first release is billed, so this one is free.",
    unknown: "A release is permanent: its values and company assigned ID are locked, and a passport's first release is recorded for billing.",
};

/**
 * The confirmation a release goes through, and the only place a cascade can be
 * asked for.
 *
 * Releasing is not a status change like revising. It is permanent, a
 * passport's first release is billed, it freezes the passport's values, and
 * it can be refused by rules that live several levels up or down the tree —
 * an ancestor that is not released yet, a cascade already running, an
 * archived passport. A dropdown that writes on selection can express none of
 * that, so this asks the server what a release WOULD do before offering to
 * do it:
 *
 *   - `can_release` false means the confirm button stays disabled and the
 *     blocker's own message is shown. The server refuses it anyway; showing the
 *     reason first is the difference between a rule and a mystery.
 *   - the per-depth counts say what sits beneath THIS passport, whatever its
 *     status; the cascade releases the drafts among them, so the checkbox
 *     names drafts and gives the count as what is below, not what is released.
 *   - the checkbox starts where the preview says it should. A depth whose
 *     instances are individually meaningful defaults to unticked; everything
 *     else defaults to cascading.
 *   - `no_change` means a revising passport has had nothing written since its
 *     release: releasing it returns to that release (`releaseNumber`, the
 *     passport's own) rather than making a new one, and the dialog says so.
 *   - `everReleased` (the core's `releases` list is not empty) decides what
 *     the dialog says about billing: only a first release is billed. Unknown
 *     (null), it words it so it is true either way.
 *
 * The preview is fetched on every open rather than cached: between one open and
 * the next, an ancestor may have been released, a child may have been created,
 * or somebody else's cascade may have started.
 */
export default function ReleaseDialog({ open, onOpenChange, snowflake, ontologyId = null, releaseNumber = 0, everReleased = null, onReleased }) {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [includeDescendants, setIncludeDescendants] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        let cancelled = false;

        setPreview(null);
        setLoading(true);

        axios.get(route('passport.release.preview', { snowflake }))
            .then(({ data }) => {
                if (cancelled) {
                    return;
                }
                setPreview(data);
                // No subtree means no cascade to ask for, whatever the default
                // says — an empty run would still be a run, and a pending run
                // freezes its chain until it closes.
                setIncludeDescendants(Boolean(data?.include_descendants_default) && (data?.total ?? 0) > 0);
            })
            .catch(() => {
                if (!cancelled) {
                    toast.error('Could not check what this release would cover');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [open, snowflake]);

    const confirm = () => {
        setSaving(true);

        axios.post(route('passport.publish_status.update', { snowflake }), {
            publish_status: 'released',
            include_descendants: includeDescendants,
        })
            .then(({ data }) => {
                toast.success('Publish status updated');
                onOpenChange(false);
                onReleased?.(data);
            })
            .catch((error) => {
                // A 409 carries the rule that refused it — an unreleased
                // ancestor names itself, a running cascade says so. That is
                // what the user can act on, so it is what they are shown.
                toast.error(error?.response?.data?.message || 'Failed to release this passport');
            })
            .finally(() => setSaving(false));
    };

    const beneath = (preview?.descendants ?? [])
        .map((level) => `${level.count} ${level.name}`)
        .join(', ');
    const below = `${preview?.total ?? 0} ${preview?.total === 1 ? 'passport' : 'passports'} below`;
    const terms = everReleased === null || everReleased === undefined
        ? RELEASE_TERMS.unknown
        : (everReleased ? RELEASE_TERMS.again : RELEASE_TERMS.first);

    return (
        <Dialog open={open} onClose={(next) => onOpenChange(Boolean(next))}>
            <DialogContent className="max-w-lg">
                <DialogTitle>Release this passport</DialogTitle>
                <DialogDescription>
                    {terms} To change values later, move the passport to Revising — the public keeps this
                    release until you release again.
                </DialogDescription>

                {loading && (
                    <p className="text-sm text-muted-foreground">Checking what this release would cover…</p>
                )}

                {preview && preview.can_release && preview.no_change && (
                    <p className="text-sm text-gray-900">{`Returns to release #${releaseNumber} — nothing changed since`}</p>
                )}

                {preview && !preview.can_release && (
                    <div role="alert" className="px-3 py-2 text-sm text-red-800 border rounded-md border-red-600/20 bg-red-50">
                        {preview.blocker?.message || 'This passport cannot be released yet.'}
                    </div>
                )}

                {preview && preview.can_release && preview.total > 0 && (
                    <label className="flex items-start gap-2 text-sm text-gray-900">
                        <Checkbox
                            className="mt-0.5"
                            checked={includeDescendants}
                            onChange={(event) => setIncludeDescendants(event.target.checked)}
                        />
                        <span>
                            {`Also release the drafts beneath (${below}: ${beneath})`}
                            {preview.truncated && ' — the count is capped, so there may be more'}
                        </span>
                    </label>
                )}

                {preview && preview.can_release && preview.total === 0 && (
                    <p className="text-sm text-muted-foreground">Nothing sits beneath this passport yet.</p>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <GatedButton
                        ability="releases:write"
                        ontologyId={ontologyId}
                        className="ml-2"
                        onClick={confirm}
                        disabled={saving || !preview || !preview.can_release}
                    >
                        Release
                    </GatedButton>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
